import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login, loginWithGoogle, loginWithFacebook } from '../api/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import { useAuthStore } from '../store/store';
import db from '../db/index';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const auth = useRef(useAuthStore(store => store.isAuth));
    const { setUser, setIsAuth } = useAuthStore(store => store);
    const navigate = useNavigate();
    const location = useLocation();

    const handleChange = e => {
        if (errorMessage) {
            setErrorMessage('');
        }
        setFormData(prevFormData => {
            return {
                ...prevFormData,
                [e.target.name]: e.target.value,
            };
        });
    };

    const handleRedirect = async uid => {
        const docRef = doc(db, 'profiles', uid);
        const docSnap = await getDoc(docRef);
        const avatarStatus = docSnap.data().created_avatar;

        const from = location.state?.from;
        if (from) {
            // user was navigated to log-in page while trying to access check-in or dashboard page without being logged in
            if (from === '/dashboard') {
                navigate(from);
            } else {
                navigate('/');
            }
        } else if (!avatarStatus) {
            navigate('/avatar');
        } else {
            navigate('/');
        }
    };

    const updateAuthStore = (user, isAuth) => {
        setUser(user);
        setIsAuth(isAuth);
    };

    const handleSubmit = async e => {
        e.preventDefault();

        try {
            const user = await login(formData);
            updateAuthStore(user, true);
            await handleRedirect(user.uid);
        } catch (e) {
            setErrorMessage(getErrorMessage(e));
        }
    };

    const getErrorMessage = e => {
        if (e instanceof Error) {
            return e.message;
        } else if (typeof e === 'string') {
            return e;
        } else {
            return 'Unknown error message';
        }
    };

    const handleGoogleLogin = async() => {
        const user = await loginWithGoogle();
        updateAuthStore(user, true);
        await handleRedirect(user.uid);
    };

    const handleFacebookLogin = async() => {
        const user = await loginWithFacebook();
        updateAuthStore(user, true);
        await handleRedirect(user.uid);
    };

    useEffect(() => {
        if (auth.current === false) {
            navigate('/login');
        } else {
            navigate('/');
        }
    }, []);

    return (
        <div className='flex justify-center flex-col my-1 items-center overflow-y-scroll min-h-[90%]'>
            <div className="bg-white rounded-4 rounded-b-3xl w-full shadow p-6 max-w-md">
                <div className="flex flex-col items-center" data-cy='headers'>
                    <h1 className="font-bold text-3xl mb-3">Log In</h1>
                    <h2 className="text-base font-normal mb-3">Please log in to proceed with our service!</h2>
                </div>

                <form onSubmit={handleSubmit}>
                    <input
                        type='email'
                        placeholder='Email Address'
                        name='email'
                        onChange={handleChange}
                        required
                        className="mb-3 w-full py-3 px-3.5 border-solid border-1 border-black rounded-3 font-400 text-4 cursor-pointer"
                        data-cy='email'
                    />

                    <input
                        type='password'
                        placeholder='Password'
                        name='password'
                        onChange={handleChange}
                        required
                        className="mb-4 w-full py-3 px-3.5 border-solid border-1 border-black rounded-2 font-400 text-4 cursor-pointer"
                        data-cy='password'
                    />

                    {errorMessage && <p className='text-red text-4 font-normal' data-cy='error-message'>{errorMessage}</p>}
                    <a
                        href=''
                        onClick={() => navigate('/forgetPassword')}
                        className='text-blue-400 font-medium cursor-pointer block text-right'
                        data-cy='forget-password-redirect'
                    >
                        Forgot Password?
                    </a>
                    <button
                        className='font-semibold w-100 py-2 px-1 rounded-3 text-4 bg-yellow mt-3 mx-auto border-black border-solid border-1'
                        data-cy='submit'
                    >
                        Confirm
                    </button>
                </form>

                <div className='flex items-center my-6 mx-0 justify-center gap-1'>
                    <hr className="w-44 border-1 border-solid border-black my-0 mx-1 grow-1"/>
                    <div className="flex items-center">
                        <p className='font-400 my-0 mx-2 text-5'>
                            Or
                        </p>
                    </div>
                    <hr className="w-44 border-1 border-solid border-black my-0 mx-1 grow-1"/>
                </div>

                <div className="flex flex-col items-center justify-center">
                    <div className="flex mb-4 gap-10">
                        <div className='py-0 border-none bg-transparent cursor-pointer' onClick={handleGoogleLogin}>
                            <FcGoogle className='w-6 h-6'/>
                        </div>
                        <div className='py-0 border-none bg-transparent cursor-pointer' onClick={handleFacebookLogin}>
                            <FaFacebook className='text-blue w-6 h-6'/>
                        </div>
                    </div>

                    <div className='flex items-center justify-center'>
                        <p className='m-0'>Don&apos;t have an account?
                            <a href='#' onClick={() => navigate('/register')} className='ml-2 text-underline' data-cy='signup-redirect'>
                                Sign Up
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
